// Price-update matching — the shared, deterministic candidate scoring plus the
// price-update-ai edge calls (extract + match) used by BOTH the Update Prices
// page (UpdatePrices.jsx) and Counter Intelligence's in-chat price-update flow.
//
// Keeping these in one place means the chat assistant runs items through the
// exact same extraction prompt and the exact same candidate scoring as the
// Catalog & Prices → Update Prices tool — there is one price-update processor,
// not two that can drift apart.
//
// The pure scoring helpers (tokenize → jaccard → scoreCandidacy → getCandidates)
// have no I/O so they are unit-testable. The edge calls take a `getHeaders`
// function so each caller supplies its own auth header builder.

export const CANDIDATE_LIMIT = 30;

// The review actions the Update Prices page offers per matched row.
export const ACTIONS = {
  UPDATE_ALL: 'update_all',
  UPDATE_PRICE: 'update_price',
  IGNORE: 'ignore',
  ADD_NEW: 'add_new',
};

// Abbreviation expansion — applied before Jaccard so "Chard" matches "Chardonnay".
const ABBREV_MAP = {
  'cab': 'cabernet', 'sauv': 'sauvignon', 'chard': 'chardonnay',
  'blanc': 'blanc', 'pinot': 'pinot', 'gris': 'grigio', 'grigio': 'grigio',
  'zin': 'zinfandel', 'syrah': 'syrah', 'shiraz': 'syrah',
  'merlot': 'merlot', 'riesling': 'riesling', 'viognier': 'viognier',
  'brut': 'brut', 'rose': 'rose', 'prosecco': 'prosecco',
  'napa': 'napa valley', 'sono': 'sonoma', 'burg': 'burgundy',
  'lb': 'pound', 'lbs': 'pound', 'oz': 'ounce', 'cs': 'case',
  'bx': 'box', 'ea': 'each', 'dz': 'dozen',
  'gal': 'gallon', 'qt': 'quart', 'pt': 'pint',
  'ground': 'ground', 'beef': 'beef', 'chicken': 'chicken',
  'lettuce': 'lettuce', 'rom': 'romaine', 'romain': 'romaine',
};

// Stop words to strip before comparison.
const STOP_WORDS = new Set(['the', 'a', 'an', 'of', 'de', 'du', 'la', 'le', 'les']);

function tokenize(str) {
  return (str || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(Boolean);
}

function expandAndNormalize(str) {
  const tokens = tokenize(str).filter((t) => !STOP_WORDS.has(t));
  return tokens.map((t) => ABBREV_MAP[t] || t);
}

export function jaccardSimilarity(a, b) {
  const setA = new Set(expandAndNormalize(a));
  const setB = new Set(expandAndNormalize(b));
  if (!setA.size && !setB.size) return 0;
  const intersection = [...setA].filter((t) => setB.has(t)).length;
  const union = new Set([...setA, ...setB]).size;
  return union === 0 ? 0 : intersection / union;
}

export function scoreCandidacy(invoiceItem, catalogItem) {
  const nameSim = jaccardSimilarity(invoiceItem.name, catalogItem.description);
  // Producer/brand is the strongest signal — weight 0.4.
  const vendorSim = invoiceItem.vendor
    ? jaccardSimilarity(invoiceItem.vendor, catalogItem.vendor || '')
    : 0.5;
  const unitSim = invoiceItem.unit
    ? jaccardSimilarity(invoiceItem.unit, (catalogItem.unit || '') + ' ' + (catalogItem.pack_size || ''))
    : 0.5;
  return nameSim * 0.4 + vendorSim * 0.4 + unitSim * 0.2;
}

export function getCandidates(invoiceItem, catalogItems, limit = CANDIDATE_LIMIT) {
  const scored = (catalogItems || []).map((cat) => ({ item: cat, score: scoreCandidacy(invoiceItem, cat) }));
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.item);
}

// ── Edge calls (price-update-ai) ─────────────────────────────────────────────

export function priceUpdateEndpoint() {
  const base = (typeof window !== 'undefined' && window.SUPABASE_URL) || 'https://hpmbxwbuoamijdyhpele.supabase.co';
  return `${base}/functions/v1/price-update-ai`;
}

// Extract line items AND invoice metadata from one uploaded source. `payload`
// carries the file shape the edge function expects ({ fileType, text } |
// { fileType:'image', mediaType, imageData } | { fileType:'pdf', text }).
// Returns { items, invoiceVendor, invoiceDate, invoiceNumber } — invoiceVendor
// is the issuing supplier and invoiceDate the date the invoice is dated
// (YYYY-MM-DD, or null); both head the approval screen, and invoiceDate also
// dates the resulting price events while invoiceNumber references them.
export async function callPriceExtractWithMeta(getHeaders, payload) {
  const headers = await getHeaders();
  const resp = await fetch(priceUpdateEndpoint(), {
    method: 'POST',
    headers,
    body: JSON.stringify({ action: 'extract', ...payload }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data?.error || `Edge function error ${resp.status}`);
  return {
    items: data?.items || [],
    invoiceVendor: data?.vendor_name || null,
    invoiceDate: data?.invoice_date || null,
    invoiceNumber: data?.invoice_number || null,
  };
}

// Roll a list of extraction-tagged items/rows up into the distinct invoices they
// came from, for the approval header ("Sysco · Invoice #1042 · 2026-07-10 · 12
// lines"). Each item carries _invoiceVendor / _invoiceNumber / _invoiceDate from
// callPriceExtractWithMeta, and (where available) a stable _sourceFile.
//
// Grouping prefers _sourceFile so a single invoice stays one group even while
// the user edits its vendor/date on the approval screen; it falls back to the
// value tuple when there's no source key. Each entry carries `sourceFile` (the
// key to target edits back at, or null) plus the first non-empty vendor / number
// / date seen. Items with no invoice metadata at all are skipped so a
// manual/undetected import doesn't render an empty summary.
export function summarizeInvoices(items) {
  const byKey = new Map();
  for (const it of (Array.isArray(items) ? items : [])) {
    // Keep raw values for display/editing (trimming here would make an editable
    // vendor field reject a mid-typed space); trim only to key and to decide
    // whether a field is present.
    const vendorRaw = it?._invoiceVendor ?? '';
    const numberRaw = it?._invoiceNumber ?? '';
    const dateRaw = it?._invoiceDate ?? '';
    const vendor = String(vendorRaw).trim();
    const number = String(numberRaw).trim();
    const date = String(dateRaw).trim();
    if (!vendor && !number && !date) continue;
    const source = it?._sourceFile != null ? String(it._sourceFile) : null;
    const key = source ?? `${vendor.toLowerCase()}|${number.toLowerCase()}|${date}`;
    if (!byKey.has(key)) {
      byKey.set(key, {
        sourceFile: source,
        vendor: vendor ? vendorRaw : null,
        invoiceNumber: number ? numberRaw : null,
        invoiceDate: date ? dateRaw : null,
        lineCount: 0,
      });
    }
    const entry = byKey.get(key);
    entry.lineCount += 1;
    // Some lines of an invoice may carry the metadata and others not; keep the
    // first non-empty value for each field.
    if (!String(entry.vendor ?? '').trim() && vendor) entry.vendor = vendorRaw;
    if (!String(entry.invoiceNumber ?? '').trim() && number) entry.invoiceNumber = numberRaw;
    if (!String(entry.invoiceDate ?? '').trim() && date) entry.invoiceDate = dateRaw;
  }
  return [...byKey.values()];
}

// Apply a user's invoice-header correction (vendor and/or date) to every line of
// the targeted invoice. `group` is a summarizeInvoices entry; `patch` may carry
// `vendor` and/or `invoiceDate`. Vendor writes through to the per-line `vendor`
// too, so the approval table, the recorded invoice_vendor, and any new items all
// agree. Returns a new array; lines outside the invoice are returned unchanged.
export function applyInvoiceFieldEdit(items, group, patch) {
  const g = group || {};
  const p = patch || {};
  return (Array.isArray(items) ? items : []).map((it) => {
    const inGroup = g.sourceFile != null
      ? it?._sourceFile === g.sourceFile
      : String(it?._invoiceVendor ?? '').trim() === String(g.vendor ?? '').trim()
        && String(it?._invoiceNumber ?? '').trim() === String(g.invoiceNumber ?? '').trim()
        && String(it?._invoiceDate ?? '').trim() === String(g.invoiceDate ?? '').trim();
    if (!inGroup) return it;
    const next = { ...it };
    if ('vendor' in p) {
      next._invoiceVendor = p.vendor;
      next.vendor = p.vendor;
    }
    if ('invoiceDate' in p) {
      next._invoiceDate = p.invoiceDate || null;
    }
    return next;
  });
}

// Format an invoice date (YYYY-MM-DD) for display as "Jul 10, 2026". Built from
// the parts, not new Date('YYYY-MM-DD') (which parses as UTC midnight and can
// render the day before in the Americas). Returns '' for a missing/invalid date.
export function formatInvoiceDateLabel(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// Back-compat wrapper: callers that only need the line items. Returns the
// extracted item array.
export async function callPriceExtract(getHeaders, payload) {
  const { items } = await callPriceExtractWithMeta(getHeaders, payload);
  return items;
}

// Ask the AI to pick the best catalog match for one invoice item from the
// pre-scored candidate list. Classification still comes from the deterministic
// dedup (importDedup.classifyItem); this only enriches the per-row review.
export async function callPriceMatch(getHeaders, invoiceItem, candidates) {
  const headers = await getHeaders();
  const resp = await fetch(priceUpdateEndpoint(), {
    method: 'POST',
    headers,
    body: JSON.stringify({ action: 'match', invoiceItem, candidates }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data?.error || `Edge function error ${resp.status}`);
  return data || { catalogId: null, confidence: 'none', suggestedAction: 'add_new', reason: '' };
}
